import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { Modal } from '~/components/overlays';
import { InlineToggleButton } from '~/components/ui/buttons/InlineToggleButton';
import {
  DeliveryAddressMap,
  type MapStyleName,
} from '~/components/common/DeliveryAddressMap';

type Point = { latitude: number; longitude: number };

type Props = {
  isOpen: boolean;
  initialLatitude: number | undefined;
  initialLongitude: number | undefined;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  onConfirm: (latitude: number, longitude: number) => void;
  onClose: () => void;
};

export function MapPickerDialog({
  isOpen,
  initialLatitude,
  initialLongitude,
  centerLatitude,
  centerLongitude,
  onConfirm,
  onClose,
}: Readonly<Props>) {
  const { t } = useTranslation('cart');
  const [candidate, setCandidate] = useState<Point | null>(null);
  const [styleName, setStyleName] = useState<MapStyleName>('Standard');

  const point =
    candidate ??
    (initialLatitude != null && initialLongitude != null
      ? { latitude: initialLatitude, longitude: initialLongitude }
      : null);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-neut-bg-alt rounded-xl shadow-xl w-[36rem] max-w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neut-border">
          <h2 className="text-base font-semibold text-neut-prim">
            {t('cart.delivery_address.map_title')}
          </h2>
        </div>
        <div className="px-6 py-4 space-y-2">
          <div className="flex justify-end gap-3">
            <InlineToggleButton
              testId="delivery-address-style-map"
              label={t('cart.delivery_address.style_map')}
              active={styleName === 'Standard'}
              onClick={() => setStyleName('Standard')}
            />
            <InlineToggleButton
              testId="delivery-address-style-satellite"
              label={t('cart.delivery_address.style_satellite')}
              active={styleName === 'Hybrid'}
              onClick={() => setStyleName('Hybrid')}
            />
          </div>
          <DeliveryAddressMap
            latitude={point?.latitude}
            longitude={point?.longitude}
            centerLatitude={centerLatitude}
            centerLongitude={centerLongitude}
            heightClassName="h-96"
            mapStyle={styleName}
            onSelect={(latitude, longitude) =>
              setCandidate({ latitude, longitude })
            }
          />
          <p className="text-sm text-neut-700 min-h-5">
            {point
              ? `${point.latitude.toFixed(7)}, ${point.longitude.toFixed(7)}`
              : ''}
          </p>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neut-border">
          <Button variant="secondary" size="sm" width="auto" onClick={onClose}>
            {t('cart.delivery_address.cancel')}
          </Button>
          <Button
            data-testid="delivery-address-use-point"
            size="sm"
            width="auto"
            disabled={point === null}
            onClick={() => {
              if (point !== null) onConfirm(point.latitude, point.longitude);
            }}
          >
            {t('cart.delivery_address.use_point')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
